<template lang="pug">
    v-layout.justify-center(row wrap)
        v-flex(xs12)
            Navbar(:noBackChevron="true")

            template(v-if="inquiryLoading")
                fan-loader.mt-5(
                    :loading="inquiryLoading"
                ) 
                    div(slot="loadingMsg"
                        class="full-width text-xs-center headline my-2"
                    ) {{ loadingMsg ? loadingMsg : null }}

            v-flex(v-else)
            
                div
                    v-img(
                        :src="wellsFargoSvg"
                        :max-width="250"
                        class="mx-auto mt-4"
                    )

                v-flex
                    v-form(v-model="isFormValid")

                        div(v-if="accountNotFound")
                            h3.mt-5 Sorry, we could not find your account
                            
                            p.mt-5 Please contact Wells Fargo
                            PhoneLink(:phoneNumber="'866-936-0602'")
                            
                            p.mt-5 - or -
                            AppNextButton.mt-5(
                                :buttonFunction="redirectToWellsFargoCreditApplication"
                            ) Start New Application

                        div.ma-5(v-else)
                            h1 Help us find your account:
                            v-text-field(
                                label="Account Number"
                                v-model="acctInfo.accountNumber"
                                :rules="[rules.required, rules.accountNumber]"
                                mask="################"
                                type="number"
                            )
                            v-text-field(
                                label="First Name"
                                v-model="acctInfo.firstName"
                                :rules="[rules.required, rules.nameValidation]"
                            )
                            v-text-field(
                                label="Last Name"
                                v-model="acctInfo.lastName"
                                :rules="[rules.required, rules.nameValidation]"
                            )
                            v-text-field(
                                label="Date of Birth"
                                v-model="acctInfo.dob"
                                mask="##/##/####"
                                placeholder="MM/DD/YYYY"
                                :rules="[rules.required, rules.dobValid]"
                                return-masked-value
                            ) 
                            v-text-field(
                                label="Email"
                                v-model="acctInfo.email"
                                :rules="[rules.required, rules.email]"
                                return-masked-value
                            )

                            AppNextButton(
                                :buttonFunction="submitInquiry"
                                :disabled="!isFormValid"
                            ) Submit

                            span(
                                v-show="accountInquiryNotFoundWarning"
                                class="red--text subheading"
                            ) Sorry, we could not find your account. Please try again.

            v-dialog(
                    v-model='dialog' 
                    persistent 
                    width='500px'
                )
                v-card
                    v-card-title.wf-justify-content-center
                        img.mx-5(
                            :src="wellsFargoSvg"
                            width="150px"
                            align="center"
                        )
                    v-card-text
                        h2 Do you have an existing Wells Fargo credit limit?
                    v-spacer
                    v-btn(
                        color='secondary' 
                        @click='proceedToInquiry()' 
                        flat
                    ) Yes
                    v-btn(
                        color='secondary' 
                        @click='redirectToWellsFargoCreditApplication()' 
                        flat
                    ) No
                    v-card-text
                        p By selecting 'Yes' I understand that I will be providing information to look up information about my active Wells Fargo credit cards, or if selecting 'No', for the purposes of applying for a credit card issued by Wells Fargo.

</template>

<script>
    import Navbar from 'Components/Navbar.vue'
    import PhoneLink from 'Components/PhoneLink.vue'
    import AppNextButton from 'Components/AppNextButton.vue'
    import AppNextFlatButton from 'Components/AppNextFlatButton.vue'
    import FanLoader from '@/components/FanLoader'
    import { mapActions, mapGetters, mapState } from 'vuex'
    import { createAppRecordFromWellsFargoAccountInquiry } from 'Helpers/c4cAPIClient'
    import { delayMilliseconds } from 'Helpers/utilFunctions.js'

    export default {
        name: 'AccountInquiryWellsFargo',
        components: {
            AppNextButton,
            AppNextFlatButton,
            FanLoader,
            Navbar,
            PhoneLink
        },
        data: () => ({
            acctInfo: {},
            accountInquiryNotFoundWarning: null,
            accountNotFound: null,
            dialog: true,
            errorCount: 0,
            isFormValid: false,
            loading: true,
            loadingAccountInquiryResults: null,
            loadingMsg: null,
            wellsFargoAccountInfo: null,
            wellsFargoSvg: require('@/assets/wellsfargo_logo.svg'),
            rules: {
                required: value => !!value || 'Required',
                accountNumber: value => /^\d{16}/.test(value) || 'Account Number must be 16 digits',
                email: value => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) || 'Invalid email',
                nameValidation: value => /^.[a-zA-Z'\s-.]*$/.test(value) || "Only allow alpha (a-z, A-Z), single quote ('), hyphen (-), and period (.) characters",
                dobValid: value => /^(?:0[1-9]|1[012])([/.-])(?:0[1-9]|[12]\d|3[01])\1(?:19|20)\d\d$/.test(value) || 'Please enter a Valid Date of Birth'
            },
        }),
        computed: {
            ...mapGetters([
                'availableLenders', 'moneyInt'
            ]),
            ...mapState({
                loanPlanId: state => state.loanApplication.loanPlan.planId,
                dealerId: state => state.user.dealerId,
                easyLinkId: state => state.easyLink.guid,
                requestedCreditLimit: state => state.loanApplication.loanAmount,
                merchantInfo: state => state.user.dealerContactInfo
            }),
            inquiryLoading() {
                return this.loading || this.loadingAccountInquiryResults
            }
        },
        methods: {
            ...mapActions([
                'getAllPlans',
                'error'
            ]),
            proceedToInquiry() {
                this.loading = false
                this.dialog = false
            },
            async redirectToWellsFargoCreditApplication() {
                this.$router.push('/credit-application/redirecting')
                const allWellsFargoPlans = await this.getAllPlans()
                const matchedPlans = allWellsFargoPlans.filter(plan => Number(plan.id) === Number(this.loanPlanId))
                if (matchedPlans.length === 1) {
                    const plan = matchedPlans[0]
                    this.$store.commit('setState', [`loanApplication.loanPlan`, plan])
                    this.$store.commit('setState', [`easyLink.startPage`, '/credit-application/form-page-one'])
                    this.$router.push('/credit-application/form-page-one')
                    this.$store.commit('openDialog', { component: 'easy-link-welcome-dialog' })
                }
            },
            async submitInquiry() {
                this.loadingMsg = 'Locating Your Account Details...'
                this.loadingAccountInquiryResults = true
                try {
                    const allWellsFargoPlans = await this.getAllPlans()
                    const matchedPlans = allWellsFargoPlans.filter(plan => Number(plan.id) === Number(this.loanPlanId))
                    let plan
                    if (matchedPlans.length === 1) {
                        plan = matchedPlans[0]
                        this.$store.commit('setState', [`loanApplication.loanPlan`, plan])
                    } else {
                        await this.error("We couldn't locate your Plan")
                    }
                    
                    const result = await createAppRecordFromWellsFargoAccountInquiry({
                        "easyLinkId": this.easyLinkId,
                        "dealerId": this.dealerId,
                        "merchantNumber": this.availableLenders.find(lender => lender.lender_code === "wellsfargo" ).merchant_id,
                        "planNumber": plan.plan_number,
                        "requestedCreditLimit": this.moneyInt(this.requestedCreditLimit),
                        "merchant": {
                            "name": this.merchantInfo.companyName,
                            "contactPersonFirstName": this.merchantInfo.fistName,
                            "contactPersonLastName": this.merchantInfo.lastName,
                            "email": this.merchantInfo.email,
                            "phoneNumber": this.merchantInfo.phoneNumber,
                            "address": this.merchantInfo.address
                        },
                        "applicantFirstName": this.acctInfo.firstName,
                        "applicantLastName": this.acctInfo.lastName,
                        "applicantEmail": this.acctInfo.email,
                        "applicantDateOfBirth": this.acctInfo.dob,
                        "accountNumber": this.acctInfo.accountNumber
                    })
                    this.loadingMsg = `Your account has been found. Redirecting to Authorization...`
                    const c4cApplicationId = result.data.applicationId
                    await delayMilliseconds(2000)
                    this.$router.push(`/credit-application/wellsfargo-results/${c4cApplicationId}?A=`)
                } catch(error) {
                    if (error.response.data.name === "AccountNotFoundError") {
                        this.errorCount += 1;
                        if (this.errorCount > 1) {
                            this.accountNotFound = true
                            this.loadingAccountInquiryResults = false
                        }
                        this.accountInquiryNotFoundWarning = true
                        this.loadingAccountInquiryResults = false
                    } else {
                        console.log({error})
                    }
                }
            },
        },
    }
</script>